// FILFlywheel.jsx
import React from 'react';

const FILFlywheel = () => {
  const merchantBenefits = [
    "Zero commission on sales — rewards given only when redeemed",
    "Extremely low-risk marketing vs traditional ads",
    "Increased visibility & ranking through attractive streak rewards",
    "Attracts loyal Gen Z/Gen Alpha customers via gamification",
    "Converts ad spend into performance-based discounts & free items",
    "Organic traffic from requests, nearby discovery & challenges",
    "Run hyperlocal flash offers, treasure hunts & FOMO campaigns",
    "Builds direct customer loyalty with streak points",
    "Participates in ecosystem challenges driving targeted traffic",
    "Tiered rewards for higher-level users"
  ];

  const customerBenefits = [
    "Quick access to hyperlocal daily essentials",
    "Easy request system for specific needs from nearby sellers",
    "Earn streak points through daily activities & reflections",
    "Redeem points for real discounts & free items",
    "Fun gamified experience with levels & streaks",
    "Discover real-time offers & fresh inventory",
    "Support local students, moms & teen entrepreneurs",
    "Social features like group ordering & community discovery",
    "Turns errands into rewarding & exciting activities",
    "Links self-improvement to real-world savings"
  ];

  return (
    <div className="min-h-screen bg-[#0a0a0a] text-white font-sans">
      {/* Header */}
      <div className="pt-6 pb-4 px-5 text-center border-b border-purple-500/20">
        <h1 className="text-xl lg:text-2xl font-bold mb-3 bg-gradient-to-r from-purple-400 via-pink-400 to-yellow-400 bg-clip-text text-transparent">
          FOUND IT LOCAL 🔥
        </h1>
        <p className="text-sm lg:text-lg text-purple-300">The Streak-Powered Hyperlocal Flywheel</p>
      </div>

      {/* Introduction */}
      <div className="max-w-4xl mx-auto px-4 py-1 text-center">
        <div className="bg-gradient-to-br from-purple-900/30 to-pink-900/30 border border-purple-500/30 rounded-3xl px-4 py-2 lg:px-5 lg:py-4">
          <p className="text-sm lg:text-lg leading-relaxed text-purple-100">
            FoundItLocal by Think5Whys is a hyperlocal request-and-discover platform designed for Gen Z and Gen Alpha users as well as ultra-local micro businesses. 
            It focuses on immediate daily-life needs through action-based tabs instead of traditional categories — powered by an innovative Think5Whys <span className="text-yellow-400 font-semibold">streak points economy</span>.
          </p>
        </div>
      </div>

      {/* Benefits to Merchants */}
      <div className="max-w-5xl mx-auto px-4 py-4">
        <h2 className="text-lg lg:text-xl text-cyan-200 font-bold mb-2 flex items-center gap-3">
          <span>🛍️</span> Benefits to Merchants
        </h2>
        <div className="grid lg:grid-cols-2 gap-1">
          {merchantBenefits.map((benefit, i) => (
            <div key={i} className="bg-zinc-900/80 hover:bg-zinc-800 border border-purple-500/20 rounded-2xl px-4 py-1 transition-all hover:border-purple-400 flex gap-4">
              <span className="text-xs lg:text-sm">✅</span>
              <p className="text-xs lg:text-sm leading-tight text-purple-100">{benefit}</p>
            </div>
          ))}
        </div>
      </div>

      {/* Benefits to Customers */}
      <div className="max-w-5xl mx-auto px-4 py-4 bg-black/40">
        <h2 className="text-lg lg:text-xl text-cyan-200 font-bold mb-2 flex items-center gap-3">
          <span>🌟</span> Benefits to Customers
        </h2>
        <div className="grid lg:grid-cols-2 gap-1">
          {customerBenefits.map((benefit, i) => (
            <div key={i} className="bg-zinc-900/80 hover:bg-zinc-800 border border-pink-500/20 rounded-2xl px-4 py-1 transition-all hover:border-pink-400 flex gap-4">
              <span className="text-xs lg:text-sm">⭐</span>
              <p className="text-xs lg:text-sm leading-tight text-pink-100">{benefit}</p>
            </div>
          ))}
        </div>
      </div>

      {/* The Flywheel Section */}
      <div className="max-w-5xl mx-auto px-5 py-4">
        <h2 className="text-lg lg:text-xl text-cyan-200 font-bold mb-10 text-center flex items-center justify-center gap-3">
          <span>🔄</span> THE FLYWHEEL
        </h2>

        <div className="space-y-2 lg:space-y-3 relative before:absolute before:left-8 lg:before:left-1/2 before:top-0 before:bottom-0 before:w-1 before:bg-gradient-to-b before:from-purple-500 before:via-pink-500 before:to-yellow-400 before:opacity-30">
          {[
            ["Subscription", "Think5Whys PurpleDoor / LifeReplay / GenZ Startup → GST to Country", "💎"],
            ["Daily Activities", "Think5Whys PurpleDoor / LifeReplay / GenZ Startup Activities", "⚡"],
            ["Earn Points", "Subscribers Earn Streak Points → Win Prizes", "⭐"],
            ["Redeem", "Redeem at Think5Whys FindItLocal / Hustles", "🛍️"],
            ["Seller Wins", "Seller Gets Customer", "🎯"],
            ["More Rewards", "Seller Creates More Rewards", "🎁"],
            ["Re-engagement", "Seller Enables Own Streak Account", "🔄"],
            ["Growth Loop", "More Users Participate and User Rechurns", "📈"],
            ["More Points", "More Streak Points Earned ← Subscribe Again", "🔥"],
            ["Final Loop", "More Redemption → Circular Streak Economy Created", "🌍"]
          ].map((step, index) => (
            <div key={index} className="relative flex lg:items-center gap-2 lg:even:flex-row-reverse">
              <div className="w-8 h-8 lg:w-10 lg:h-10 flex-shrink-0 bg-gradient-to-br from-purple-600 to-pink-600 rounded-2xl flex items-center justify-center text-xl lg:text-2xl shadow-xl border-4 border-black z-10">
                {step[2]}
              </div>
              <div className="bg-zinc-900/90 border border-white/10 rounded-2xl px-4 py-1 lg:px-8 lg:py-2 flex-1">
                <h3 className="text-sm lg:text-lg font-bold mb-2 text-purple-300">{step[0]}</h3>
                <p className="text-purple-100 text-xs lg:text-sm leading-relaxed">{step[1]}</p>
              </div>
            </div>
          ))}
        </div>

        {/* <div className="mt-10 text-center bg-gradient-to-r from-emerald-900/50 to-cyan-900/50 border border-emerald-500/30 rounded-3xl p-6">
          <p className="text-emerald-300 font-medium">
            No selling commission charged to sellers • Subscription starts at <span className="font-bold text-white">₹50</span> first quarter (India + GST)
          </p>
        </div> */}
      </div>

      {/* Recommendation Section */}
      <div className="max-w-4xl mx-auto px-4 py-4 bg-gradient-to-b from-transparent via-purple-950/30 to-transparent">
        <h2 className="text-lg lg:text-xl text-cyan-200 font-bold mb-2 text-center">💡 Recommendation</h2>
        <div className="bg-zinc-900/80 border border-yellow-400/20 rounded-3xl px-4 py-1 lg:px-6 lg:py-2 text-center lg:text-left">
          <p className="text-sm lg:text-lg leading-relaxed text-yellow-100">
            Focus on <span className="font-bold text-yellow-400">continuous engagement and fun</span>. 
            Emphasize gamification through daily streaks, levels, community challenges, treasure hunts, and seasonal campaigns. 
            Tie streak points to positive daily habits like reflection, healthy eating, local exploration, and supporting micro businesses.
          </p>
          <p className="text-sm lg:text-lg mt-2 text-purple-300">
            Use personalized “Today’s Action” feeds and social sharing to make routine local needs exciting and habitual — 
            creating a joyful loop where users improve themselves, support their neighborhood, save money, and stay consistently engaged.
          </p>
        </div>
      </div>

      <footer className="text-center py-4 text-xs text-purple-500 border-t border-white/10">
        FoundItLocal by Think5Whys • Built for Digital Generation Consumers • Powered by Think5Whys Streak Economy ❤️
      </footer>
    </div>
  );
};

export default FILFlywheel;